import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useToast from 'contexts/ToastProvider/useToast';
import useNoPermissionToast from 'hooks/useNoPermissionToast';
import SettingsCard from 'modules/SettingsModal/SettingsCard/SettingsCard';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import logger from 'utils/logging/faro';
import { Schemas } from 'utils/yup/schemas/validationSchemas';

type WorkspaceNameFormValues = yup.InferType<ReturnType<typeof Schemas.getWorkspaceNameValidation>>;

const WorkspaceNameSetting = () => {
  const { t } = useTranslation();
  const { matrixClient } = useMatrixContext();
  const { workspaceId, workspacesState, workspacePermissions } = useWorkspacesContext({ requireWorkspaceId: true });
  const { pushToast } = useToast();
  const pushNoPermissionToast = useNoPermissionToast();
  const [isLoading, setIsLoading] = useState(false);

  const currentName = workspacesState.workspaces[workspaceId].name;

  const onSubmit = async ({ workspaceName }: WorkspaceNameFormValues) => {
    if (!workspacePermissions.workspaceName) return pushNoPermissionToast();

    try {
      setIsLoading(true);
      await matrixClient.setRoomName(workspaceId, workspaceName);
      pushToast({ variant: 'success', title: t('workspaces.workspace_name_updated') });
    } catch (error) {
      logger.error('Error updating workspace name:', error);
      pushToast({ variant: 'error', title: t('something_went_wrong_try_again_later') });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SettingsCard.Content>
      <div className="flex flex-col space-y-4">
        <div className="flex flex-col space-y-1">
          <SettingsCard.Content.Title>{t('workspaces.workspace_name')}</SettingsCard.Content.Title>
          <SettingsCard.Content.Description>
            {t('workspaces.workspace_name_change_desc')}
          </SettingsCard.Content.Description>
        </div>
        <SettingsCard.Content.Form
          schema={Schemas.getWorkspaceNameValidation()}
          defaultValue={currentName}
          onSubmit={onSubmit}
          fieldName="workspaceName"
          loading={isLoading}
          placeholder={t('workspaces.enter_workspace_name')}
          containerClassName="flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-2"
          buttonClassName="!ml-0 sm:!ml-2 max-w-[302px]"
        />
      </div>
    </SettingsCard.Content>
  );
};

export default WorkspaceNameSetting;
